import React, { useState, useEffect } from 'react';
import { MessageSquare, AlertCircle, CheckCircle, Clock, ChevronRight, Users } from 'lucide-react';
import { complaintAPI } from '../utils/api';
import DateRangeFilter from './DateRangeFilter';

export function ComplaintsStatsCard({ isOfficer = false, onOfficerClick }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(30);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showOfficers, setShowOfficers] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [selectedDays, customStartDate, customEndDate]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await complaintAPI.getStats(selectedDays, customStartDate, customEndDate);
      setStats(res.data);
    } catch (err) {
      console.log('Could not fetch complaints stats');
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (days) => {
    setSelectedDays(days);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  const handleCustomDateChange = (start, end) => {
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="complaints-stats-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <MessageSquare className="text-orange-500" size={20} />
          {isOfficer ? 'My Complaints' : 'Complaints Overview'}
        </h3>
        <DateRangeFilter
          selectedDays={selectedDays}
          onFilterChange={handleFilterChange}
          startDate={customStartDate}
          endDate={customEndDate}
          onCustomDateChange={handleCustomDateChange}
          compact={true}
        />
      </div>

      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-20 bg-slate-100 rounded-lg"></div>
          <div className="h-16 bg-slate-100 rounded-lg"></div>
        </div>
      ) : stats ? (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{stats.total_complaints}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <p className="text-2xl font-bold text-red-600">{stats.open_complaints}</p>
              <p className="text-xs text-slate-500">Open</p>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg">
              <p className="text-2xl font-bold text-amber-600">{stats.in_progress_complaints}</p>
              <p className="text-xs text-slate-500">In Progress</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{stats.resolved_complaints}</p>
              <p className="text-xs text-slate-500">Resolved</p>
            </div>
          </div>

          {/* Type Breakdown */}
          {stats.type_breakdown && Object.keys(stats.type_breakdown).length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-slate-700 mb-2">By Type</p>
              <div className="space-y-2">
                {Object.entries(stats.type_breakdown).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm text-slate-700">{type}</span>
                    <span className="text-sm font-semibold text-slate-900">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Officer Breakdown (for admins) */}
          {!isOfficer && stats.officer_breakdown && stats.officer_breakdown.length > 0 && (
            <div>
              <button
                onClick={() => setShowOfficers(!showOfficers)}
                className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-all"
                data-testid="toggle-officer-complaints"
              >
                <div className="flex items-center gap-2">
                  <Users size={18} className="text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">By Case Officer</span>
                </div>
                <ChevronRight size={18} className={`text-slate-400 transition-transform ${showOfficers ? 'rotate-90' : ''}`} />
              </button>
              
              {showOfficers && (
                <div className="mt-2 space-y-2" data-testid="officer-complaints-list">
                  {stats.officer_breakdown.map((officer) => (
                    <div 
                      key={officer.officer_id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                      onClick={() => onOfficerClick && onOfficerClick(officer.officer_id)}
                    >
                      <div>
                        <p className="font-medium text-slate-900">{officer.officer_name}</p>
                        <p className="text-xs text-slate-500">
                          {officer.open} open, {officer.resolved} resolved
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-slate-900">{officer.total_complaints}</p>
                        <p className="text-xs text-slate-500">total</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* No complaints message */}
          {stats.total_complaints === 0 && (
            <div className="text-center py-6">
              <CheckCircle size={40} className="mx-auto text-green-300 mb-2" />
              <p className="text-slate-500">No complaints in this period</p>
              <p className="text-sm text-green-600 mt-1">Great job!</p>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-6">
          <AlertCircle size={40} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">Could not load complaints data</p>
        </div>
      )}
    </div>
  );
}

export function ComplaintsStatBadge({ count, status = 'total' }) {
  const colors = {
    total: 'bg-slate-100 text-slate-700',
    open: 'bg-red-100 text-red-700',
    'in_progress': 'bg-amber-100 text-amber-700',
    resolved: 'bg-green-100 text-green-700'
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status]}`}>
      {count}
    </span>
  );
}
